import styled from 'styled-components'

import { breakpoints } from '@/config/layout'
import { isProduction } from '@/utils/environment'
import { Span } from '../../typography/Body'

const StAppHeaderContainer = styled.div<{ backgroundColor?: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space2};
  padding-bottom: 0px;
  background-color: ${({ backgroundColor }) => backgroundColor || null};
  z-index: 10;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};

  @media ${breakpoints.fromS} {
    height: auto;
    gap: ${({ theme }) => theme.UI.SpacingPx.Space1};
    padding: ${({ theme }) => theme.UI.SpacingPx.Space8}
      ${({ theme }) => theme.UI.SpacingPx.Space12};
  }
`

const StAppHeaderContentContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};

  @media ${breakpoints.fromS} {
    padding: 0;
  }
`

const StHeaderSide = styled.div<{ $align?: 'right' }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'flex-start')};
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  min-width: 0;
`

const StAppPrimaryActionContainer = styled.div<{ titleIsVisible: boolean }>`
  flex: 0 0 auto;
`

const StAppHeaderAction = styled.button<{ reverse?: boolean }>`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space1};

  ${({ reverse }) =>
    reverse &&
    `
  text-align: right;
  justify-content: flex-end;
  `}

  &:hover {
    cursor: pointer;
  }
  color: ${({ theme }) => theme.Components.Button.Tertiary.Text};

  > svg + span {
    margin-left: ${({ theme }) => theme.UI.SpacingPx.Space2};
  }

  > span + svg {
    margin-left: ${({ theme }) => theme.UI.SpacingPx.Space2};
  }
`

const StHeaderAction = styled.div`
  ${({ theme }) => `
    z-index: 2;
    width: max-content;
    height: ${theme.UI.SpacingPx.Space6};
    display: flex;
    gap: ${theme.UI.SpacingPx.Space4};
  `}
`

const StProgressTextContainer = styled.div`
  color: ${({ theme }) => theme.Colors.Neutrals.Dark};
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};

  @media ${breakpoints.m} {
    margin-top:  ${({ theme }) =>
      isProduction() ? `${theme.UI.SpacingPx.Space4}` : `20px + ${theme.UI.SpacingPx.Space4}`}) ;
    }
    `

const StProgress = styled(Span)`
  flex-shrink: 0;

  @media ${breakpoints.m} {
    ${({ theme }) => `
      padding: ${theme.UI.SpacingPx.Space2} ${theme.UI.SpacingPx.Space4};
      background-color: ${theme.Colors.Neutrals.Lighter};
      border-radius: 50px;
    `}
  }
`

const StProgressBarContainer = styled.div`
  /* Slightly thinner bar in the mobile header */
  @media ${breakpoints.toS} {
    > div {
      height: 4px;
    }
  }
`

const StInlineProgressBar = styled.div`
  flex: 1;
  min-width: 0;

  @media ${breakpoints.toS} {
    /* Fixed width, centered in the header — the presence of the left/right
       buttons must not affect the bar's width or position (Figma 22438:65705) */
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    /* 50vw (≈188px on a 375px-wide design frame) */
    width: 50vw;
    flex: none;

    /* Slightly thinner bar in the mobile header */
    > div {
      height: 3px;
    }
  }

  @media ${breakpoints.m} {
    > div {
      margin-top: 0;
    }
  }
`

const StInlineTitle = styled.div`
  flex: 1;
  min-width: 0;
  text-align: center;
`

const StInlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space4};
  /* Keep the actions pinned right even when there is no left button — the
     inline bar is absolutely positioned and doesn't reserve space in the row */
  margin-left: auto;
`

export {
  StAppHeaderAction,
  StAppHeaderContainer,
  StAppHeaderContentContainer,
  StAppPrimaryActionContainer,
  StHeaderAction,
  StHeaderSide,
  StInlineActions,
  StInlineProgressBar,
  StInlineTitle,
  StProgress,
  StProgressBarContainer,
  StProgressTextContainer,
}
